import React from 'react';
import './TagsHandler.css';

interface Tag {
  tag_type: string;
  tag_value: string | boolean;
}

interface TagsHandlerProps {
  tags: string | Tag[] | string[];
  variant?: 'default' | 'primary' | 'secondary';
  size?: 'small' | 'medium' | 'large';
  showType?: boolean;
  className?: string;
  onTagClick?: ((tag: Tag) => void) | null;
}

const TagsHandler: React.FC<TagsHandlerProps> = ({
  tags,
  variant = 'default',
  size = 'medium',
  showType = true,
  className = '',
  onTagClick = null,
}) => {
  // Parse tags if it's a string, otherwise use as array
  const parseTags = (): Tag[] => {
    try {
      if (typeof tags === 'string') {
        const trimmed = tags.trim();
        if (
          !trimmed ||
          trimmed.toLowerCase() === 'null' ||
          trimmed.toLowerCase() === 'undefined'
        ) {
          return [];
        }
        // JSON array string: e.g., '[{"tag_type":"status","tag_value":"boosted"}]' or '["boosted"]'
        if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
          const parsed = JSON.parse(trimmed);
          if (Array.isArray(parsed)) {
            // If it's a simple array of strings, map to objects
            if (parsed.length > 0 && typeof parsed[0] === 'string') {
              return parsed.map(v => ({ tag_type: 'status', tag_value: v }));
            }
            return parsed;
          }
        }
        // Simple string like 'boosted' or 'blocked'
        return [{ tag_type: 'status', tag_value: trimmed }];
      }
      if (Array.isArray(tags)) {
        // If array of strings, convert to objects
        if (tags.length > 0 && typeof tags[0] === 'string') {
          return (tags as string[]).map(v => ({
            tag_type: 'status',
            tag_value: v,
          }));
        }
        return tags as Tag[];
      }
      return [];
    } catch (error) {
      console.error('Error parsing tags:', error);
      return [];
    }
  };

  const tagsList = parseTags();

  // Get tag variant class based on tag type and value
  const getTagVariant = (
    tagType: string,
    tagValue: string | boolean
  ): string => {
    // Handle boolean-like values for any tag type
    if (tagValue === 'true' || tagValue === true) {
      return 'tags-handler__tag--success';
    }
    if (tagValue === 'false' || tagValue === false) {
      return 'tags-handler__tag--danger';
    }

    // Handle common status values across any tag type
    if (
      [
        'resolved',
        'completed',
        'approved',
        'success',
        'active',
        'enabled',
        'boosted',
      ].includes(tagValue?.toLowerCase())
    ) {
      return 'tags-handler__tag--success';
    }
    if (
      ['pending', 'in_progress', 'processing', 'warning', 'review'].includes(
        tagValue?.toLowerCase()
      )
    ) {
      return 'tags-handler__tag--warning';
    }
    if (
      [
        'rejected',
        'failed',
        'error',
        'inactive',
        'disabled',
        'blocked',
      ].includes(tagValue?.toLowerCase())
    ) {
      return 'tags-handler__tag--danger';
    }

    // Handle priority values
    if (['high', 'urgent', 'critical'].includes(tagValue?.toLowerCase())) {
      return 'tags-handler__tag--danger';
    }
    if (['medium', 'normal'].includes(tagValue?.toLowerCase())) {
      return 'tags-handler__tag--warning';
    }
    if (['low', 'minor'].includes(tagValue?.toLowerCase())) {
      return 'tags-handler__tag--info';
    }

    // Handle specific tag types
    switch (tagType) {
      case 'question_type':
      case 'type':
      case 'category':
        return 'tags-handler__tag--info';
      case 'is_appropriate':
      case 'is_valid':
      case 'is_correct':
        // Already handled above with boolean values
        return 'tags-handler__tag--default';
      default:
        return 'tags-handler__tag--default';
    }
  };

  // Format tag display text
  const formatTagText = (
    tagType: string,
    tagValue: string | boolean
  ): string => {
    if (showType === false) {
      return String(tagValue);
    }
    // Default behavior: show both type and value in a readable format
    const formattedType = tagType
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    return `${formattedType}: ${tagValue}`;
  };

  // Handle tag click
  const handleTagClick = (tag: Tag): void => {
    if (onTagClick && typeof onTagClick === 'function') {
      onTagClick(tag);
    }
  };

  if (!tagsList || tagsList.length === 0) {
    return null;
  }

  return (
    <div className={`tags-handler ${className}`}>
      {tagsList.map((tag, index) => {
        const { tag_type, tag_value } = tag;
        const variantClass = getTagVariant(tag_type, tag_value);
        const sizeClass = `tags-handler__tag--${size}`;
        const clickableClass = onTagClick ? 'tags-handler__tag--clickable' : '';

        return (
          <span
            key={index}
            className={`tags-handler__tag ${variantClass} ${sizeClass} ${clickableClass}`}
            onClick={() => handleTagClick(tag)}
            title={showType === false ? `${tag_type}: ${tag_value}` : undefined}
          >
            {formatTagText(tag_type, tag_value)}
          </span>
        );
      })}
    </div>
  );
};

export default TagsHandler;
